import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CompanySearchInput } from "@/components/company-search";
import { CompanyDetails } from "@/lib/api/companyRegister";
import { useContractTypes } from "@/hooks/useContractTypes";
import { useCategories } from "@/hooks/useCategories";
import { ContractType, Category } from "@/lib/types";
import { UseFormReturn } from "react-hook-form";
import { ContractFormValues } from "./ContractFormSchema";
import { 
  IncomingIcon, 
  OutgoingIcon, 
  EmploymentIcon, 
  CircleDotIcon,
  DirectionText
} from "@/components/contract-types/ContractDirectionIcons";

interface ContractBasicDetailsProps {
  form: UseFormReturn<ContractFormValues>;
  selectedContractType: ContractType | null;
  setSelectedContractType: (type: ContractType | null) => void;
  setContractTypeModalOpen: (open: boolean) => void;
  setCategoryModalOpen: (open: boolean) => void;
  showCompanySearch?: boolean;
  setShowCompanySearch?: (show: boolean) => void;
  handleCompanySelect: (company: CompanyDetails) => CompanyDetails;
  handleContractTypeCreated: (contractType: ContractType) => void;
  handleCategoryCreated: (category: Category) => void;
}

export function ContractBasicDetails({
  form,
  selectedContractType,
  setSelectedContractType,
  setContractTypeModalOpen,
  setCategoryModalOpen,
  handleCompanySelect,
  handleContractTypeCreated,
  handleCategoryCreated
}: ContractBasicDetailsProps) {
  const { useContractTypesQuery } = useContractTypes();
  const { data: contractTypes = [] } = useContractTypesQuery();
  
  const { useCategoriesQuery } = useCategories();
  const { data: categories = [] } = useCategoriesQuery();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Contract Details</h3>
        <p className="text-sm text-muted-foreground">
          Basic information about the contract
        </p>
      </div>

      <FormField
        control={form.control}
        name="supplierName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Supplier Name</FormLabel>
            <FormControl>
              <CompanySearchInput 
                defaultValue={field.value}
                onChange={field.onChange}
                onSelect={(company) => {
                  const updatedCompany = handleCompanySelect(company);
                  field.onChange(updatedCompany.name);
                }}
                className="w-full"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="organisationnumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Organization Number</FormLabel>
            <FormControl>
              <Input placeholder="Enter organization number" {...field} />
            </FormControl>
            <FormDescription>
              The official identifier for the supplier company (recommended)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="importance"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Importance</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(parseInt(value))}
              value={field.value.toString()}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select importance" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">Very Low</SelectItem>
                <SelectItem value="2">Low</SelectItem>
                <SelectItem value="3">Medium</SelectItem>
                <SelectItem value="4">High</SelectItem>
                <SelectItem value="5">Critical</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contractTypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              Contract Type
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-2 p-1">
                      <p className="text-sm font-semibold">Contract Directions:</p>
                      <div className="flex items-center gap-2">
                        <IncomingIcon className="h-4 w-4 text-emerald-500" />
                        <span className="text-xs">Incoming - You receive payments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <OutgoingIcon className="h-4 w-4 text-orange-500" />
                        <span className="text-xs">Outgoing - You make payments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <EmploymentIcon className="h-4 w-4 text-purple-500" />
                        <span className="text-xs">Employment - Salary related</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CircleDotIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-xs">Other - Miscellaneous contracts</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                const selectedType = contractTypes.find(type => type.id === value) || null;
                setSelectedContractType(selectedType);
              }}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select contract type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {contractTypes && contractTypes.length > 0 ? (
                  contractTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        {type.direction === "incoming" && <IncomingIcon className="h-4 w-4 text-emerald-500" />}
                        {type.direction === "outgoing" && <OutgoingIcon className="h-4 w-4 text-orange-500" />}
                        {type.direction === "employment" && <EmploymentIcon className="h-4 w-4 text-purple-500" />}
                        {(!type.direction || type.direction === "other") && <CircleDotIcon className="h-4 w-4 text-blue-500" />}
                        <span>{type.name}</span>
                        {type.auto_renews && (
                          <span className="ml-2 text-xs text-primary">
                            (Auto-renews)
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-types-available" disabled>No contract types available</SelectItem>
                )}
              </SelectContent>
            </Select>
            <div className="flex flex-col space-y-2">
              {selectedContractType?.auto_renews && (
                <FormDescription className="flex items-center text-primary">
                  <RefreshCw className="h-4 w-4 mr-1" />
                  <span>
                    Auto-renews every {selectedContractType.renewal_interval} {selectedContractType.renewal_unit.toLowerCase()}
                    {selectedContractType.renewal_interval > 1 ? 's' : ''}
                  </span>
                </FormDescription>
              )}
              {selectedContractType && (
                <FormDescription className="text-sm">
                  <DirectionText direction={selectedContractType.direction} />
                </FormDescription>
              )}
              {selectedContractType?.direction === "employment" && (
                <p className="text-sm text-muted-foreground mt-1">
                  Employment contracts have special handling for expiry dates.
                </p>
              )}
              {selectedContractType?.direction === "other" && (
                <p className="text-sm text-muted-foreground mt-1">
                  Consider specifying a more specific contract direction.
                </p>
              )}
              <FormDescription>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-xs"
                  onClick={(e) => {
                    e.preventDefault();
                    setContractTypeModalOpen(true);
                  }}
                >
                  + Add new contract type
                </Button>
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="categoryId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Category</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-categories-available" disabled>No categories available</SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormDescription>
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  setCategoryModalOpen(true);
                }}
              >
                + Add new category
              </Button>
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
